import axios from 'axios';

import { normalizeDigits } from '../utils/normalizeDigits';

interface ZipCloudResult {
  zipcode?: string | null;
  prefcode?: string | null;
  address1?: string | null;
  address2?: string | null;
  address3?: string | null;
  kana1?: string | null;
  kana2?: string | null;
  kana3?: string | null;
}

interface ZipCloudResponse {
  message?: string | null;
  results?: ZipCloudResult[] | null;
  status: number;
}

const isZipCloudResponse = (value: unknown): value is ZipCloudResponse =>
  Boolean(
    value &&
      typeof value === 'object' &&
      'status' in value &&
      typeof (value as { status?: unknown }).status === 'number',
  );

export interface PostalLookupResult {
  prefecture: string;
  city: string;
  addressLine1: string;
}

export interface PostalLookupOptions {
  signal?: AbortSignal;
  endpoint?: string;
}

const isPostalLookupResultPayload = (value: unknown): value is PostalLookupResult =>
  Boolean(
    value &&
      typeof value === 'object' &&
      'prefecture' in value &&
      'city' in value &&
      'addressLine1' in value,
  );

export type PostalLookupErrorType = 'api_error' | 'network';

export class PostalLookupError extends Error {
  readonly type: PostalLookupErrorType;
  readonly status?: number;

  constructor(message: string, type: PostalLookupErrorType, status?: number) {
    super(message);
    this.name = 'PostalLookupError';
    this.type = type;
    this.status = status;
  }
}

const buildDefaultEndpoint = (): string => {
  const apiBase = (process.env.REACT_APP_API_URL ?? '').trim();
  if (!apiBase) {
    return '/api/postal-lookup';
  }
  return `${apiBase.replace(/\/+$/, '')}/api/postal-lookup`;
};

const DEFAULT_ENDPOINT = buildDefaultEndpoint();

export const lookupPostalCode = async (
  postalCode: string,
  options?: PostalLookupOptions,
): Promise<PostalLookupResult | null> => {
  const normalized = normalizeDigits(postalCode);
  const digits = normalized.replace(/[^0-9]/g, '');

  if (digits.length !== 7) {
    throw new PostalLookupError('郵便番号は7桁の数字で指定してください。', 'api_error');
  }

  try {
    const endpoint = options?.endpoint ?? DEFAULT_ENDPOINT;
    const paramsKey = endpoint.includes('zipcloud') ? 'zipcode' : 'postalCode';
    const response = await axios.get(endpoint, {
      params: { [paramsKey]: digits },
      signal: options?.signal,
    });

    const data = response.data as unknown;

    if (data == null) {
      return null;
    }

    if (isZipCloudResponse(data)) {
      if (data.status !== 200) {
        if (data.status === 400) {
          return null;
        }
        throw new PostalLookupError(
          data.message ?? '郵便番号検索に失敗しました。時間をおいて再試行してください。',
          'api_error',
          data.status,
        );
      }

      const result = data.results?.[0] ?? null;
      if (!result) {
        return null;
      }

      const prefecture = typeof result.address1 === 'string' ? result.address1.trim() : '';
      const city = typeof result.address2 === 'string' ? result.address2.trim() : '';
      const addressLine1 = typeof result.address3 === 'string' ? result.address3.trim() : '';

      return {
        prefecture,
        city,
        addressLine1,
      };
    }

    if (isPostalLookupResultPayload(data)) {
      const prefecture = typeof data.prefecture === 'string' ? data.prefecture.trim() : '';
      const city = typeof data.city === 'string' ? data.city.trim() : '';
      const addressLine1 =
        typeof data.addressLine1 === 'string' ? data.addressLine1.trim() : '';

      if (!prefecture && !city && !addressLine1) {
        return null;
      }

      return {
        prefecture,
        city,
        addressLine1,
      };
    }

    throw new PostalLookupError('郵便番号検索の応答が不正です。', 'api_error');
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    if (error instanceof PostalLookupError) {
      throw error;
    }
    if (axios.isAxiosError(error)) {
      const { response } = error;
      const status = response?.status;

      if (response) {
        const responseData = response.data as
          | { message?: string; detail?: string }
          | undefined;
        const message =
          (typeof responseData?.message === 'string' && responseData.message.trim()) ||
          (typeof responseData?.detail === 'string' && responseData.detail.trim()) ||
          error.message ||
          '住所検索に失敗しました。時間をおいて再試行してください。';
        throw new PostalLookupError(message, 'api_error', status);
      }

      const message =
        error.message || '住所検索に失敗しました。時間をおいて再試行してください。';
      throw new PostalLookupError(message, 'network', status);
    }

    throw new PostalLookupError(
      '住所検索に失敗しました。時間をおいて再試行してください。',
      'network',
    );
  }
};
